import { useEffect, useRef, useState } from "react";
import { Card, ErrorBanner, Pill, Tile, fmt, recipientsLabel } from "../components/ui";
import { api, type Blast, type Campaign, type SendConfig } from "../lib/api";

export function Today() {
  const [cfg, setCfg] = useState<SendConfig | null>(null);
  const [blasts, setBlasts] = useState<Blast[]>([]);
  const [camps, setCamps] = useState<Campaign[]>([]);
  const [error, setError] = useState<string | null>(null);

  // One round at a time. Under load these three have taken tens of seconds,
  // and a five-second interval that fires anyway stacks copies of them.
  const loading = useRef(false);
  const load = () => {
    if (loading.current) return Promise.resolve();
    loading.current = true;
    return Promise.all([api.sendConfig(), api.blasts(), api.campaigns()])
      .then(([c, b, k]) => { setCfg(c); setBlasts(b); setCamps(k); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => {
        loading.current = false;
      });
  };

  // Polling, not websockets. A connection would drop on every deploy and on
  // every instance recycle; a 3-second poll of one row is immune to both.
  useEffect(() => {
    load();
    // Three endpoints every five seconds, for as long as a tab is open. A
    // background tab is nobody watching, so it stops; coming back reloads at
    // once, which is what makes it safe to stop.
    const t = setInterval(() => {
      if (!document.hidden) load();
    }, 5000);
    const wake = () => {
      if (!document.hidden) load();
    };
    document.addEventListener("visibilitychange", wake);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", wake);
    };
  }, []);

  const running = blasts.filter((b) => ["running", "paused"].includes(b.status));
  const inFlight = camps.reduce((n, c) => n + c.active_enrollments, 0);

  return (
    <>
      <ErrorBanner error={error} />

      {cfg?.kill_switch && (
        <div className="banner bad">
          <b>Sending is stopped.</b>
          <span>{cfg.kill_switch_reason ?? "The kill switch is on."}</span>
          <div className="sp" />
          <button className="btn s" onClick={() => api.killSwitch(false).then(load)}>
            Resume sending
          </button>
        </div>
      )}

      <div className="row w">
        <div>
          <h2 style={{ fontSize: 19 }}>Right now</h2>
          <span className="lab">updating every 5 seconds</span>
        </div>
        <div className="sp" />
        <a className="btn p" href="/blasts/new">New blast</a>
        {!cfg?.kill_switch && (
          <button
            className="btn d"
            onClick={() => {
              const reason = prompt("Why are you stopping all sending?");
              if (reason !== null) api.killSwitch(true, reason).then(load);
            }}
          >
            Stop all sending
          </button>
        )}
      </div>

      <div className="tiles">
        <Tile label="Sent today" value={fmt.n(cfg?.sent_today)} detail={`ceiling ${fmt.n(cfg?.daily_ceiling)}`} />
        <Tile label="In a campaign" value={fmt.n(inFlight)} detail={`${camps.length} campaigns`} />
        <Tile label="Blasts running" value={fmt.n(running.length)} tone={running.length ? "ok" : undefined} />
        <Tile label="Sending speed" value={`${cfg?.texts_per_minute ?? 0}/min`} detail="set in Settings" />
      </div>

      <Card title="Blasts" actions={<a className="btn s" href="/blasts">All blasts</a>}>
        {running.length === 0 ? (
          <div className="empty">Nothing sending right now.</div>
        ) : (
          running.map((b) => (
            <div className="row w" key={b.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--line2)" }}>
              <div style={{ minWidth: 200 }}>
                <b>{b.name}</b>
                <div className="dim mono" style={{ fontSize: 11 }}>
                  {b.status} · {b.rate_per_minute}/min
                </div>
              </div>
              <div className="sp" />
              {/* count_sent is only written when a blast completes and nothing
                  writes count_replied at all, so both read 0 for exactly as
                  long as a blast is running - which is the whole time this
                  card is on screen. The live figures are counted off the queue
                  by /progress, which the Blasts screen polls; repeating that
                  poll here would double the cost of watching one blast. */}
              <span className="mono dim">
                {b.build_at_send && b.total_recipients === 0
                  ? `${recipientsLabel(b)} · list built at send`
                  : `${recipientsLabel(b)} recipients`}
              </span>
              <a className="btn s" href={`/blasts`}>How far along</a>
            </div>
          ))
        )}
      </Card>

      <Card title="Where everyone is">
        {camps.length === 0 ? (
          <div className="empty">No campaigns yet.</div>
        ) : (
          camps.map((c) => (
            <div className="brk" key={c.id} style={{ fontFamily: "var(--sans)", padding: "8px 0" }}>
              <span>
                {c.name}{" "}
                <Pill tone={c.status === "active" ? "ok" : "nu"}>{c.status}</Pill>
              </span>
              <span className="mono">{fmt.n(c.active_enrollments)}</span>
            </div>
          ))
        )}
      </Card>
    </>
  );
}
